import React from 'react'
import styled from '@emotion/styled'

import {isolateContentDirection} from '@vio/atlas/src/utils/truncate'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'

interface PlaceOfSearch {
  label: string
  href?: string
}

interface HotelName {
  label: string
  href?: string
}

interface Props {
  /** Place of search from SRP (Search Results Page) */
  placeOfSearch?: PlaceOfSearch
  /** Hotel name from AP (Accommodation Page) - only renders when provided */
  hotelName?: HotelName
  /** Pass through classname to allow styles overrides */
  className?: string
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: ${cssTheme.layout.spacing.s200};
  white-space: nowrap;
  overflow: hidden;
  ${linariaMq.desktopXs} {
    gap: ${cssTheme.layout.spacing.s250};
  }
  p {
    overflow: hidden;
    text-overflow: ellipsis;
    ${isolateContentDirection()}
  }
`

const Separator = styled.span`
  color: ${cssTheme.colors.content.neutral.secondary};
`

const Link = styled.a`
  text-decoration: none;
  color: ${cssTheme.colors.content.interactive.primary};

  &:hover {
    text-decoration: underline;
  }
`

export const Breadcrumbs = ({placeOfSearch, hotelName, className}: Props) => {
  const breadcrumbItems = [
    {
      label: 'Reise',
      href: 'https://www.finn.no/reise',
      isLink: true,
      target: '_blank'
    },
    {
      label: 'Hotell',
      href: '/',
      isLink: true,
      target: '_self'
    }
  ]

  if (placeOfSearch) {
    breadcrumbItems.push({
      label: placeOfSearch.label,
      href: placeOfSearch.href || '',
      isLink: hotelName ? true : false,
      target: '_self'
    })
  }

  if (hotelName) {
    breadcrumbItems.push({
      label: hotelName.label,
      href: hotelName.href || '',
      isLink: Boolean(hotelName.href),
      target: '_self'
    })
  }

  return (
    <Container className={className} data-id="breadcrumbs">
      {breadcrumbItems.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Separator>
              <Text variant="bodyS">/</Text>
            </Separator>
          )}
          {item.isLink ? (
            <Link href={item.href} target={item.target}>
              <Text variant="bodyS" dir="auto">
                {item.label}
              </Text>
            </Link>
          ) : (
            <Text variant="bodyS" dir="auto">
              {item.label}
            </Text>
          )}
        </React.Fragment>
      ))}
    </Container>
  )
}
